import React from "react";
import {
  Box,
  Button,
  FormHelperText,
  IconButton,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import { LIMITES, VENTA_MESA, TIPO_COMPLEMENTO, paqueteVacio, complementoVacio } from "../../domain/tarifa/schema";

const PRIMARIO = "#5656ff";
const TITULO = "#0b0062";
const SUAVE = "#6b7280";

const Subtitulo = ({ children, ayuda }) => (
  <Box sx={{ mt: 2, mb: 1 }}>
    <Typography sx={{ fontSize: 14, fontWeight: 600, color: TITULO }}>{children}</Typography>
    {ayuda && <Typography sx={{ fontSize: 12, color: SUAVE }}>{ayuda}</Typography>}
  </Box>
);

const Fila = ({ children, onQuitar }) => (
  <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1 }}>
    {children}
    <IconButton size="small" onClick={onQuitar} aria-label="Quitar">
      <Delete fontSize="small" />
    </IconButton>
  </Box>
);

// ConfigMesaTarifa: cómo vende las mesas un ticket cuya ubicación tiene mesas.
// tarifasTickets son las demás entradas del evento, para los complementos.
const ConfigMesaTarifa = ({ form, set, error, tarifasTickets, ubicacion }) => {
  const capacidades = (ubicacion.mesas || []).map((m) => m.capacidad_total || m.asientos?.length || 0);
  const minSillas = Math.min(...capacidades);
  const maxSillas = Math.max(...capacidades);
  const opciones = tarifasTickets.filter((t) => t.id !== form.id);

  const cambiarPaquete = (indice, cambios) =>
    set("paquetes", form.paquetes.map((p, i) => (i === indice ? { ...p, ...cambios } : p)));
  const cambiarComplemento = (indice, cambios) =>
    set("complementos", form.complementos.map((c, i) => (i === indice ? { ...c, ...cambios } : c)));
  const quitarComplemento = (indice) => set("complementos", form.complementos.filter((_, i) => i !== indice));

  const listaComplementos = (tipo) =>
    form.complementos.map((c, indice) => ({ ...c, indice })).filter((c) => c.tipo === tipo);

  const selectorTarifa = (c) => (
    <TextField
      select
      size="small"
      label="Tarifa"
      value={c.idtarifa}
      onChange={(e) => cambiarComplemento(c.indice, { idtarifa: e.target.value })}
      sx={{ flex: 1, minWidth: 0 }}
    >
      {opciones.length === 0 && <MenuItem disabled value="">No hay otras entradas en el evento</MenuItem>}
      {opciones.map((t) => (
        <MenuItem key={t.id} value={t.id}>
          {t.concepto}
        </MenuItem>
      ))}
    </TextField>
  );

  const textoMesas =
    minSillas === maxSillas ? `Las mesas de esta ubicación tienen ${maxSillas} sillas.` : `Las mesas de esta ubicación tienen de ${minSillas} a ${maxSillas} sillas.`;

  return (
    <Box sx={{ mt: 1, mb: 2, p: 2, border: "1px solid #e4e4e7", borderRadius: 2 }}>
      <Typography sx={{ fontSize: 15, fontWeight: 600, color: TITULO, mb: 1 }}>Venta de mesas</Typography>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={form.venta_mesa}
        onChange={(_, v) => v && set("venta_mesa", v)}
        sx={{ "& .MuiToggleButton-root": { textTransform: "none", px: 2 }, "& .Mui-selected": { color: `${PRIMARIO} !important` } }}
      >
        <ToggleButton value={VENTA_MESA.SILLA}>Por silla</ToggleButton>
        <ToggleButton value={VENTA_MESA.MESA}>Mesa entera</ToggleButton>
      </ToggleButtonGroup>
      <Typography sx={{ fontSize: 12, color: SUAVE, mt: 1 }}>
        {form.venta_mesa === VENTA_MESA.MESA
          ? `Se vende una entrada por mesa y todas sus sillas quedan ocupadas. ${textoMesas}`
          : "Cada silla se vende como una entrada aparte."}
      </Typography>

      {form.venta_mesa === VENTA_MESA.MESA && (
        <>
          <Subtitulo ayuda="Precio según para cuántas personas es la mesa. Sin paquetes, la mesa sale el precio de la tarifa.">
            Paquetes
          </Subtitulo>
          {form.paquetes.map((p, indice) => (
            <Box key={indice}>
              <Fila onQuitar={() => set("paquetes", form.paquetes.filter((_, i) => i !== indice))}>
                <TextField
                  size="small"
                  type="number"
                  label="Personas"
                  value={p.personas}
                  onChange={(e) => cambiarPaquete(indice, { personas: e.target.value })}
                  sx={{ width: 110 }}
                />
                <TextField
                  size="small"
                  type="number"
                  label="Precio"
                  value={p.precio}
                  onChange={(e) => cambiarPaquete(indice, { precio: e.target.value })}
                  sx={{ flex: 1 }}
                />
              </Fila>
              {Number(p.personas) > maxSillas && (
                <Typography sx={{ fontSize: 12, color: "#b45309", mt: -0.5, mb: 1 }}>
                  No entra en ninguna mesa: sumá sillas extra como adicional.
                </Typography>
              )}
            </Box>
          ))}
          <Button
            size="small"
            startIcon={<Add />}
            onClick={() => set("paquetes", [...form.paquetes, paqueteVacio()])}
            disabled={form.paquetes.length >= LIMITES.PAQUETES_MAX}
            sx={{ textTransform: "none", color: PRIMARIO }}
          >
            Agregar paquete
          </Button>

          <Subtitulo ayuda="Otra entrada que se suma a la mesa, con un máximo por mesa: menor, silla extra…">
            Adicionales
          </Subtitulo>
          {listaComplementos(TIPO_COMPLEMENTO.ADICIONAL).map((c) => (
            <Fila key={c.indice} onQuitar={() => quitarComplemento(c.indice)}>
              {selectorTarifa(c)}
              <TextField
                size="small"
                type="number"
                label="Máx. por mesa"
                value={c.maximo_por_mesa}
                onChange={(e) => cambiarComplemento(c.indice, { maximo_por_mesa: e.target.value })}
                sx={{ width: 120 }}
              />
            </Fila>
          ))}
          <Button
            size="small"
            startIcon={<Add />}
            onClick={() => set("complementos", [...form.complementos, complementoVacio(TIPO_COMPLEMENTO.ADICIONAL)])}
            disabled={form.complementos.length >= LIMITES.COMPLEMENTOS_MAX}
            sx={{ textTransform: "none", color: PRIMARIO }}
          >
            Agregar adicional
          </Button>

          <Subtitulo ayuda="Entradas que se ofrecen para sumar en la misma compra, como la general del día.">
            Entradas sugeridas
          </Subtitulo>
          {listaComplementos(TIPO_COMPLEMENTO.SUGERIDA).map((c) => (
            <Fila key={c.indice} onQuitar={() => quitarComplemento(c.indice)}>
              {selectorTarifa(c)}
            </Fila>
          ))}
          <Button
            size="small"
            startIcon={<Add />}
            onClick={() => set("complementos", [...form.complementos, complementoVacio(TIPO_COMPLEMENTO.SUGERIDA)])}
            disabled={form.complementos.length >= LIMITES.COMPLEMENTOS_MAX}
            sx={{ textTransform: "none", color: PRIMARIO }}
          >
            Agregar entrada sugerida
          </Button>
        </>
      )}
      {error && <FormHelperText error>{error}</FormHelperText>}
    </Box>
  );
};

export default ConfigMesaTarifa;
